/**
 * Units table rendered inside an expanded FlexiblePropertyCard.
 *
 * Fetches the listing's units itself; per-unit status and tenants are lazy-loaded by their
 * own cells when the row renders.
 *
 * TODO:
 * - Add empty states and wire the overflow menu actions to real handlers.
 */
import { useRef } from 'react';
import Link from 'next/link';

import {
  Anchor,
  Box,
  Button,
  Paragraph,
  PropertyCard,
  Table,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation-10';

import { useFlexiblePropertiesSearch } from '../../../contexts/FlexiblePropertiesSearchContext';
import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import { useGetPropertyTableUnits } from '../../../hooks/queries/propertyTable';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { getPhotoThumbUrl } from '../../../utils/flexiblePropertyCardUtils';
import numberUtils from '../../../utils/numberUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import { getDashboardOverviewRoute } from '../../../constants/urls';

import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import {
  hiddenMobileCellStyles,
  lastRowStyles,
  overflowMenuCellStyle,
  propertyRowLinkStyles,
  tableCellContentWidths,
  truncatedTextStyles,
  unitCellContentStyles,
  unitCellStyles,
  unitInfoStyles,
  unitThumbnailStyles,
} from '../styles';

import SkeletonUnitRows from './SkeletonUnitRows';
import StatusCell from './StatusCell';
import TenantsCell from './TenantsCell';
import UnitOverflowMenu from './UnitOverflowMenu';

import type {
  AccountViewExpandedPropertyUnitResponseDto,
  AccountViewListingGroupDto,
  RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// Row shape mirrors the API unit DTO.
export type UnitRow = AccountViewExpandedPropertyUnitResponseDto;

// Secondary columns are hidden on small screens, keeping only Unit and the always-visible
// overflow menu (which has no visible header label).
const UNIT_TABLE_COLUMNS: Array<{
  label: string;
  hideOnMobile?: boolean;
  hideLabel?: boolean;
  width?: (typeof tableCellContentWidths)[keyof typeof tableCellContentWidths];
}> = [
  { label: 'Unit', width: tableCellContentWidths.UNIT },
  { label: 'Rent', hideOnMobile: true, width: tableCellContentWidths.RENT },
  { label: 'Status', hideOnMobile: true, width: tableCellContentWidths.STATUS },
  { label: 'Tenants', hideOnMobile: true, width: tableCellContentWidths.TENANTS },
  { label: 'Overflow menu', hideLabel: true, width: tableCellContentWidths.OVERFLOW },
];

// Only Unit + overflow are visible on mobile; all 5 columns are visible from lg up.
const MOBILE_VISIBLE_COLUMN_COUNT = 2;

type Props = {
  listing: AccountViewListingGroupDto;
  onArchiveClick: (listingAlias: string, address: RentalManagerAddressDto) => void;
};

const UnitsTable = ({ listing, onArchiveClick }: Props) => {
  const { isScreenMobile } = useScreenSize();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { showFixedScrollbar, isTableBottomVisible } = useTableObserver({ scrollContainerRef, isScreenMobile });
  const loadMoreColSpan = isScreenMobile ? MOBILE_VISIBLE_COLUMN_COUNT : UNIT_TABLE_COLUMNS.length;

  const groupId = listing.groupId;

  const searchText = useFlexiblePropertiesSearch();

  const apiParams = {
    searchText,
    limit: PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT,
  };

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetPropertyTableUnits(apiParams, [groupId]);

  const unitsData = dataByGroupId[groupId];

  const units = unitsData?.units ?? [];

  const rows: Array<UnitRow> = units;
  const hasNextPage = hasNextPageByGroupId[groupId] ?? false;
  const isFetching = isFetchingByGroupId[groupId] ?? false;
  const isFetchingNextPage = isFetchingNextPageByGroupId[groupId] ?? false;
  const isLoading = isFetching && units.length === 0;

  const handleLoadMore = () => {
    const endKey = unitsData?.pager?.endKey;
    if (endKey) {
      fetchNextPageForGroup(groupId, endKey);
    }
  };

  // Interactive cells (e.g. rent/tenants CTAs, popovers, overflow menu) shouldn't follow the row's
  // link (preventDefault) or bubble up to other click handlers (stopPropagation).
  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const renderUnitRow = (row: UnitRow, rowIndex: number) => {
    // When a load-more row follows, it becomes the visually last row instead.
    const isLastRow = rowIndex === rows.length - 1 && !hasNextPage;
    const route = getDashboardOverviewRoute({ listingAlias: row.listingAlias });
    const unitLabel = row.unitNumber || 'Unit';

    return (
      <Anchor key={row.listingAlias} asChild aria-label={unitLabel} css={propertyRowLinkStyles}>
        <Link href={route}>
          <Table.Row css={{ cursor: 'pointer', ...(isLastRow ? lastRowStyles : {}) }}>
            {/* unit */}
            <Table.Cell css={unitCellStyles}>
              <Box css={unitCellContentStyles}>
                <Box css={unitThumbnailStyles}>
                  <PropertyCard.Photo
                    src={getPhotoThumbUrl(row.photoKey)}
                    alt={unitLabel}
                    fallbackImg={<PropertyCard.PhotoFallback />}
                    css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
                <Box css={unitInfoStyles}>
                  <TruncatedTextWithTooltip
                    text={row.unitNumber || '—'}
                    textStyle="body-sm-bold"
                    css={truncatedTextStyles}
                  />
                  <TruncatedTextWithTooltip
                    text={`${row.bedroomCount ?? '—'} bd • ${row.bathroomCount ?? '—'} ba • ${
                      row.squareFeet ?? '—'
                    } sqft`}
                    textStyle="body-xs"
                    color="text.subtle"
                    css={truncatedTextStyles}
                  />
                </Box>
              </Box>
            </Table.Cell>

            {/* rent */}
            <Table.Cell css={hiddenMobileCellStyles} onClick={stopRowNavigation}>
              {row.rent ? (
                <Paragraph textStyle="body-sm">
                  {row.isTotalMonthlyPrice
                    ? `${numberUtils.asCurrency(row.rent)} (Total monthly price)`
                    : numberUtils.asCurrency(row.rent)}
                </Paragraph>
              ) : (
                <TextButton>Add rent</TextButton>
              )}
            </Table.Cell>

            {/* status */}
            <Table.Cell css={hiddenMobileCellStyles}>
              <StatusCell listingAlias={row.listingAlias} />
            </Table.Cell>

            {/* tenants */}
            <Table.Cell css={hiddenMobileCellStyles} onClick={stopRowNavigation}>
              <TenantsCell listingAlias={row.listingAlias} />
            </Table.Cell>

            {/* overflow menu (stop the click here from following the row's link) */}
            <Table.Cell css={overflowMenuCellStyle} onClick={stopRowNavigation}>
              <UnitOverflowMenu
                listingAlias={row.listingAlias}
                unitNumber={row.unitNumber}
                address={listing.address}
                onArchiveClick={onArchiveClick}
              />
            </Table.Cell>
          </Table.Row>
        </Link>
      </Anchor>
    );
  };

  return (
    <Box css={{ overflow: { base: 'auto', lg: 'hidden' } }}>
      <Box ref={scrollContainerRef} css={{ overflowX: { base: 'hidden', lg: 'auto' }, maxWidth: '100%' }}>
        <Table.Root
          data-testid="UnitsTable"
          aria-label="Units"
          appearance="horizontal"
          css={{ width: '100%', tableLayout: 'fixed' }}
        >
          {/* header */}
          <Table.Header>
            <Table.Row>
              {UNIT_TABLE_COLUMNS.map((column, index) => (
                <Table.HeaderCell
                  key={column.label}
                  css={{
                    ...(index === 0 ? unitCellStyles : {}),
                    ...(column.hideOnMobile ? hiddenMobileCellStyles : {}),
                    ...(column.width ?? {}),
                  }}
                >
                  {column.hideLabel ? (
                    <VisuallyHidden>{column.label}</VisuallyHidden>
                  ) : (
                    <Paragraph textStyle="body-sm-bold">{column.label}</Paragraph>
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {isLoading && <SkeletonUnitRows />}

            {!isLoading && rows.map(renderUnitRow)}

            {/* load more */}
            {!isLoading && hasNextPage && (
              <Table.Row data-testid="UnitsTable-LoadMoreRow" css={lastRowStyles}>
                <Table.Cell colSpan={loadMoreColSpan} css={{ padding: '400', textAlign: 'center' }}>
                  <Box css={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Button
                      tone="brand"
                      emphasis="outlined"
                      size="sm"
                      onClick={handleLoadMore}
                      loading={isFetchingNextPage}
                    >
                      Load more units
                    </Button>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </Box>
  );
};

export default UnitsTable;
